import { API_URL } from "env";
import { ConfirmUploadAPIResponse, CreateFolderAPIResponse, CreateFolderInput, DownloadFileAPIResponse, GeneratePresignedUrlAPIResponse, GetLibraryContentInput, GetShareDetailsAPIResponse, LibraryContentAPIResponse, UpdateFolderInput, uploadFileResponseAPI, UserWithEmail } from "./type";

function getAuthToken() {
    const token = localStorage.getItem('token');
    if (!token) {
        throw new Error('No auth token found');
    }
    return token;
}

// Folder

async function getLibraryContent(input: GetLibraryContentInput): Promise<LibraryContentAPIResponse> {
    const { parent_id, is_public } = input;
    try {
        const searchParams = new URLSearchParams();

        if (parent_id !== undefined && parent_id !== null) {
            searchParams.append('parent_id', String(parent_id));
        }
        if (is_public !== undefined && is_public !== null) {
            searchParams.append('tab', is_public ? 'system' : 'mine');
        }

        const queryString = searchParams.toString();
        const response = await fetch(`${API_URL}/api/v1/be/library/contents${queryString ? `?${queryString}` : ''}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            }
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching library content:', error);
        throw error;
    }
}


async function createFolder(input: CreateFolderInput): Promise<CreateFolderAPIResponse> {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/folders`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify(input)
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error creating folder:', error);
        throw error;
    }
}


async function deleteFolder(folder_id: number) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/folders/${folder_id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            }
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error deleting folder:', error);
        throw error;
    }
}

async function updateFolder(folder_id: number, input: UpdateFolderInput) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/folders/${folder_id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify(input)
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error updating folder:', error);
        throw error;
    }
}

async function assignAccess_Folder(folder_id: number, user_id: number) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/folders/assign-access`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify({
                folder_id,
                user_id
            })
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error assigning access:', error);
        throw error;
    }
}

async function revokeAccess_Folder(folder_id: number, user_id: number) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/folders/revoke-access`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify({ folder_id, user_id })
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error revoking access:', error);
        throw error;
    }
}

// File 

// decrypted 
// Upload flow: 1) Generate presigned URL -> 2) Upload file to presigned URL -> 3) Confirm upload
async function generatePresignedUrl(file_name: string, file: File, folder_id: number | null): Promise<GeneratePresignedUrlAPIResponse> {
    try {
        const body: Record<string, unknown> = {
            "filename": file_name,
            "content_type": file.type,
            "size": file.size,
        };
        if (folder_id !== null) {
            body["folder_id"] = folder_id;
        }
        const response = await fetch(`${API_URL}/api/v1/be/documents/presigned-url`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify(body)
        });
        const data = await response.json();
        return data as GeneratePresignedUrlAPIResponse;
    } catch (error) {
        console.error('Error generating presigned URL:', error);
        throw error;
    }
}

// decrypted 
async function uploadFileToPresignedUrl(file: File, presignedUrl: string, headers: Record<string, string>) {
    try {
        const response = await fetch(presignedUrl, {
            method: 'PUT',
            headers: {
                ...headers,
                'Content-Type': file.type
            },
            body: file
        });
        if (!response.ok) {
            throw new Error(`Failed to upload file: ${response.statusText}`);
        }
    } catch (error) {
        console.error('Error uploading file to presigned URL:', error);
        throw error;
    }
}

// Not decrypted, for future use when we want to support direct upload without presigned URL
async function uploadFile(file: File, folder_id: number | null): Promise<uploadFileResponseAPI> {
    try {
        const formData = new FormData();
        formData.append('file', file);
        if (folder_id !== null) {
            formData.append('folder_id', String(folder_id));
        }
        const response = await fetch(`${API_URL}/api/v1/be/documents/upload`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: formData
        });
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error('Error uploading file:', error);
        throw error;
    }
}

async function confirmFileUpload(file_name: string, file: File, folder_id: number | null, path: string): Promise<ConfirmUploadAPIResponse> {
    try {
        const body: Record<string, unknown> = {
            name: file_name,
            size: file.size,
            path,
        };
        if (folder_id !== null) {
            body['folder_id'] = folder_id;
        }
        const response = await fetch(`${API_URL}/api/v1/be/documents/confirm`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify(body)
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error confirming file upload:', error);
        throw error;
    }
}

async function downloadFile(file_id: string) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/documents/${file_id}/download`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`
            }
        });
        if (!response.ok) {
            throw new Error(`Failed to download file: ${response.statusText}`);
        }
        const data = await response.json() as DownloadFileAPIResponse;
        const downloadUrl = data.data.download_url;
        window.open(downloadUrl, '_blank');
    } catch (error) {
        console.error('Error downloading file:', error);
        throw error;
    }
}

async function openFile(file_id: string) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/documents/${file_id}/stream`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`
            }
        });
        if (!response.ok) {
            throw new Error(`Failed to open file: ${response.statusText}`);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
    } catch (error) {
        console.error('Error opening file:', error);
        throw error;
    }
}


async function deleteFile(file_id: number) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/documents/${file_id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            }
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error deleting file:', error);
        throw error;
    }
}

async function updateFile(file_id: number, name: string, folder_id: number | null) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/documents/${file_id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify({ name, folder_id })
        });
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error('Error updating file:', error);
        throw error;
    }
}

async function assignAccess_File(document_id: number, user_id: number) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/documents/assign-access`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify({
                document_id,
                user_id
            })
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error assigning access:', error);
        throw error;
    }
}

async function revokeAccess_File(document_id: number, user_id: number) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/documents/revoke-access`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify({ document_id, user_id })
        });
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error('Error revoking access:', error);
        throw error;
    }
}

async function getUser(search: string): Promise<UserWithEmail[]> {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/users/search?email=${encodeURIComponent(search)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            }
        });
        const data = await response.json() as UserWithEmail[];
        return data;
    } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
    }
}

async function streamFile(file_id: string, onData: (chunk: Uint8Array) => void, onComplete: () => void, onError: (error: any) => void) {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/documents/${file_id}/stream`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`
            }
        });
        if (!response.ok) {
            throw new Error(`Failed to stream file: ${response.statusText}`);
        }
        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('Failed to get reader from response body');
        }
        const readChunk = async () => {
            const { done, value } = await reader.read();
            if (done) {
                onComplete();
                return;
            }
            if (value) {
                onData(value);
            }
            readChunk();
        }
        readChunk();
    } catch (error) {
        console.error('Error streaming file:', error);
        onError(error);
    }
}
async function getShareDetail(id: number, type: 'folder' | 'file'): Promise<GetShareDetailsAPIResponse | null> {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/${type === 'folder' ? 'folders' : 'documents'}/${id}/shared-users`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthToken()}`
            }
        });
        const data = await response.json();
        return data;
    } catch(error) {
        console.error('Error fetching share detail:', error);
        throw error;
    }
}



export {
    // Folder
    getLibraryContent,
    createFolder,
    deleteFolder,
    updateFolder,
    assignAccess_Folder,
    revokeAccess_Folder,
    // File
    generatePresignedUrl,
    uploadFileToPresignedUrl,
    uploadFile,
    confirmFileUpload,
    downloadFile,
    updateFile,
    deleteFile,
    assignAccess_File,
    revokeAccess_File,
    getUser,
    streamFile,
    getShareDetail,
    openFile
}